import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  Tab,
  Tabs,
  TablePagination,
  TextField,
  Typography,
} from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import RefreshIcon from "@mui/icons-material/Refresh";
import MaterialReactTable from "material-react-table";
import { MRT_Localization_ES } from "material-react-table/locales/es";
import moment from "moment";
import Navbar from "../../Navbar";
import { logEventos } from "../../../services/services";
import { formatCuit, formatMoneda } from "../pago_proveedores/utils";
import { getMenuesUsuario } from "../cheques_proveedores/ApiServices";
import ModalAltaRechazo from "../cheques_proveedores/ModalAltaRechazo";
import {
  extraerChequesFisicos,
  extraerHistorialFisico,
  getChequeFisicoById,
  getChequesFisicos,
  getChequesFisicosResumen,
  getSucursalesChequesFisicos,
  postRechazoChequeFisico,
} from "./ApiServices";
import ModalDepositar from "./ModalDepositar";
import ModalEnviarCC from "./ModalEnviarCC";
import ModalRecibirCC from "./ModalRecibirCC";
import ModalFaltante from "./ModalFaltante";
import ModalCorregir from "./ModalCorregir";
import {
  ALERTA_VENCIMIENTO_CHEQUE,
  ALERTA_VENCIMIENTO_FILTRO,
  CUSTODIA,
  CUSTODIA_FILTRO,
  ESTADOS_FILTRO_FISICOS,
  MENU_CHEQUES_FISICOS,
  ORIGEN_CHEQUE_FILTRO,
  VISTA_CC,
  badgesDesdeResumenFisicos,
  abrevOrigenCheque,
  colorAlertaVencimiento,
  colorCustodia,
  colorEstadoFisico,
  colorOrigenCheque,
  esCasaCentral,
  esChequePropio,
  filaSxAlertaVencimiento,
  idFilaCheque,
  idSucursalDe,
  idInstrumentoFisico,
  labelAccionFisico,
  labelAlertaVencimiento,
  labelCustodia,
  labelEstadoFisico,
  labelOrigenCheque,
  nombreSucursalDe,
  queryOrigenCheque,
  normalizarRutaMenu,
  sucursalesAutorizadas,
} from "./chequesFisicos.constants";
import {
  MENU_LISTA_NEGRA,
  rutaListaNegra,
} from "../cheques_lista_negra/listaNegra.constants";

const PAGE_SIZE = 50;

const formatearFecha = (value) => {
  if (!value) return "—";
  const m = moment(value);
  return m.isValid() ? m.format("DD/MM/YYYY") : "—";
};

const formatearFechaHora = (value) => {
  if (!value) return "—";
  const m = moment(value);
  return m.isValid() ? m.format("DD/MM/YYYY HH:mm") : "—";
};

const diasDesdeEmision = (cheque) => {
  if (cheque?.dias_desde_emision != null && cheque.dias_desde_emision !== "") {
    const n = Number(cheque.dias_desde_emision);
    return Number.isFinite(n) ? n : null;
  }
  const f = cheque?.fecha_emision || cheque?.created_at;
  if (!f) return null;
  const m = moment(f);
  if (!m.isValid()) return null;
  return moment().startOf("day").diff(m.clone().startOf("day"), "days");
};

const filtrosIniciales = () => ({
  estado: "",
  estado_custodia: "",
  alerta_vencimiento: "",
  origen_cheque: "",
  id_sucursal_origen: "",
  q: "",
  fecha_pago_desde: "",
  fecha_pago_hasta: "",
});

const accionPermitida = (cheque, ...keys) => {
  for (const key of keys) {
    const v = cheque?.acciones?.[key];
    if (typeof v === "boolean") return v;
  }
  return false;
};

const custodiaDe = (cheque) =>
  cheque?.estado_custodia || cheque?.custodia || "";

const puedeDepositar = (cheque) =>
  !esChequePropio(cheque) && accionPermitida(cheque, "puede_depositar");

const puedeEnviarCc = (cheque) =>
  !esChequePropio(cheque) &&
  accionPermitida(cheque, "puede_enviar_cc", "puede_enviar_a_cc");

const puedeRecibirCc = (cheque) =>
  accionPermitida(cheque, "puede_recibir_cc");

const puedeFaltante = (cheque) =>
  accionPermitida(cheque, "puede_faltante", "puede_marcar_faltante");

const puedeCorregir = (cheque) =>
  accionPermitida(cheque, "puede_corregir");

const puedeAltaRechazo = (cheque) =>
  accionPermitida(
    cheque,
    "puede_rechazar",
    "puede_registrar_rechazo",
    "puede_alta_rechazo",
    "puede_rechazo"
  );

const CampoDetalle = ({ label, value }) => (
  <Grid item xs={12} sm={6}>
    <Typography variant="caption" color="text.secondary" display="block">
      {label}
    </Typography>
    <Typography variant="body2">{value ?? "—"}</Typography>
  </Grid>
);

const sucursalLabelCheque = (cheque) =>
  cheque?.sucursal_nombre ||
  cheque?.nombre_sucursal ||
  cheque?.sucursal ||
  (cheque?.id_sucursal != null ? `Sucursal ${cheque.id_sucursal}` : "—");

export default function ListadoChequesFisicos() {
  const [menus, setMenus] = useState({
    cartera: false,
    listaNegra: false,
  });
  const [sucursales, setSucursales] = useState([]);
  const [idSucursal, setIdSucursal] = useState("");
  const [vistaCc, setVistaCc] = useState(VISTA_CC.CARTERA);
  const [cargandoSucursales, setCargandoSucursales] = useState(true);
  const [filtros, setFiltros] = useState(filtrosIniciales);
  const [filtrosAplicados, setFiltrosAplicados] = useState(filtrosIniciales);
  const [page, setPage] = useState(0);
  const [cheques, setCheques] = useState([]);
  const [paginacion, setPaginacion] = useState({
    total: 0,
    limit: PAGE_SIZE,
    offset: 0,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [detalle, setDetalle] = useState(null);
  const [historial, setHistorial] = useState([]);
  const [loadingDetalle, setLoadingDetalle] = useState(false);
  const [errorDetalle, setErrorDetalle] = useState("");
  const [rowSelection, setRowSelection] = useState({});
  const [modalDepositar, setModalDepositar] = useState({
    open: false,
    cheque: null,
  });
  const [modalEnviar, setModalEnviar] = useState({ open: false, cheques: [] });
  const [modalRecibir, setModalRecibir] = useState({ open: false, cheques: [] });
  const [modalFaltante, setModalFaltante] = useState({
    open: false,
    cheque: null,
  });
  const [modalCorregir, setModalCorregir] = useState({
    open: false,
    cheque: null,
  });
  const [modalAltaRechazo, setModalAltaRechazo] = useState({
    open: false,
    cheque: null,
  });
  const [toast, setToast] = useState({
    open: false,
    message: "",
    severity: "info",
  });
  const [badges, setBadges] = useState({
    alerta_vencimiento_proximo: 0,
    alerta_vencimiento_vencido: 0,
  });

  const esCasaCentralActiva = esCasaCentral(idSucursal);
  const esVistaTransito =
    esCasaCentralActiva && vistaCc === VISTA_CC.TRANSITO;

  const sucursalActiva = useMemo(
    () =>
      sucursales.find((s) => String(idSucursalDe(s)) === String(idSucursal)) ||
      null,
    [sucursales, idSucursal]
  );

  useEffect(() => {
    if (!esCasaCentralActiva && vistaCc !== VISTA_CC.CARTERA) {
      setVistaCc(VISTA_CC.CARTERA);
    }
  }, [esCasaCentralActiva, vistaCc]);

  useEffect(() => {
    let cancelado = false;
    (async () => {
      try {
        const lista = await getMenuesUsuario();
        if (cancelado) return;
        const rutas = new Set(
          (Array.isArray(lista) ? lista : []).map((m) =>
            normalizarRutaMenu(m?.ruta)
          )
        );
        setMenus({
          cartera: rutas.has(MENU_CHEQUES_FISICOS),
          listaNegra: rutas.has(MENU_LISTA_NEGRA),
        });
      } catch {
        if (!cancelado) {
          setMenus({ cartera: false, listaNegra: false });
        }
      }
    })();
    return () => {
      cancelado = true;
    };
  }, []);

  useEffect(() => {
    logEventos(
      "acceso al menú Cheques físicos",
      "Proveedores",
      "Cheques",
      localStorage.getItem("user")
    );
  }, []);

  const cargarResumen = useCallback(async () => {
    try {
      const data = await getChequesFisicosResumen();
      setBadges(badgesDesdeResumenFisicos(data));
    } catch {
      // badges opcionales: no bloquear listado
    }
  }, []);

  const cargarSucursales = useCallback(async () => {
    setCargandoSucursales(true);
    try {
      const lista = await getSucursalesChequesFisicos();
      setSucursales(lista);
      setIdSucursal((prev) => {
        if (prev) return prev;
        if (!lista.length) return "";
        const sucLocal = Number(localStorage.getItem("sucursal"));
        const auth = sucursalesAutorizadas();
        const match =
          lista.find((s) => idSucursalDe(s) === sucLocal) ||
          lista.find((s) => auth.includes(idSucursalDe(s))) ||
          lista[0];
        const id = idSucursalDe(match);
        return id != null ? String(id) : "";
      });
    } catch (err) {
      setError(err.message || "No se pudieron cargar las sucursales");
      setSucursales([]);
    } finally {
      setCargandoSucursales(false);
    }
  }, []);

  useEffect(() => {
    cargarResumen();
  }, [cargarResumen]);

  useEffect(() => {
    let cancelado = false;
    (async () => {
      if (cancelado) return;
      await cargarSucursales();
    })();
    return () => {
      cancelado = true;
    };
  }, [cargarSucursales]);

  const cargar = useCallback(async () => {
    if (!esVistaTransito && !idSucursal) {
      setCheques([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const data = await getChequesFisicos({
        estado: filtrosAplicados.estado,
        alerta_vencimiento: filtrosAplicados.alerta_vencimiento,
        origen_cheque: filtrosAplicados.origen_cheque,
        q: filtrosAplicados.q,
        fecha_pago_desde: filtrosAplicados.fecha_pago_desde,
        fecha_pago_hasta: filtrosAplicados.fecha_pago_hasta,
        estado_custodia: esVistaTransito
          ? CUSTODIA.EN_TRANSITO_CC
          : filtrosAplicados.estado_custodia,
        id_sucursal: esVistaTransito
          ? filtrosAplicados.id_sucursal_origen || undefined
          : idSucursal,
        limit: PAGE_SIZE,
        offset: page * PAGE_SIZE,
      });
      setCheques(extraerChequesFisicos(data));
      setPaginacion({
        total: Number(data.total ?? extraerChequesFisicos(data).length),
        limit: Number(data.limit ?? PAGE_SIZE),
        offset: Number(data.offset ?? page * PAGE_SIZE),
      });
    } catch (err) {
      setError(err.message || "Error al cargar cheques físicos");
      setCheques([]);
    } finally {
      setLoading(false);
    }
  }, [esVistaTransito, idSucursal, filtrosAplicados, page]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const aplicarFiltros = (e) => {
    e?.preventDefault?.();
    setPage(0);
    setRowSelection({});
    setFiltrosAplicados({ ...filtros });
  };

  const limpiarFiltros = () => {
    const vacio = filtrosIniciales();
    setFiltros(vacio);
    setFiltrosAplicados(vacio);
    setPage(0);
    setRowSelection({});
  };

  const aplicarFiltroAlerta = (tipo) => {
    const valor =
      filtrosAplicados.alerta_vencimiento === tipo ? "" : tipo;
    const next = { ...filtros, alerta_vencimiento: valor };
    setFiltros(next);
    setFiltrosAplicados(next);
    setPage(0);
    setRowSelection({});
  };

  const textoSucursal = (sucursal) => {
    if (!sucursal) return "";
    const extra =
      sucursal.tiene_recaudadora === false ? " · solo banco" : "";
    return `${nombreSucursalDe(sucursal)}${extra}`;
  };

  const labelSucursalConAlertas = (sucursal) => {
    const proximo = Number(sucursal?.cheques_alerta_proximo ?? 0);
    const vencido = Number(sucursal?.cheques_alerta_vencido ?? 0);
    return (
      <Stack
        direction="row"
        alignItems="center"
        spacing={0.75}
        sx={{ width: "100%" }}
      >
        <Box component="span" sx={{ flex: 1 }}>
          {textoSucursal(sucursal)}
        </Box>
        {proximo > 0 ? (
          <Chip
            label={proximo > 99 ? "99+" : proximo}
            size="small"
            color="warning"
            sx={{ height: 20, "& .MuiChip-label": { px: 0.75, fontSize: 11 } }}
          />
        ) : null}
        {vencido > 0 ? (
          <Chip
            label={vencido > 99 ? "99+" : vencido}
            size="small"
            color="error"
            sx={{ height: 20, "& .MuiChip-label": { px: 0.75, fontSize: 11 } }}
          />
        ) : null}
      </Stack>
    );
  };

  const abrirDetalle = useCallback(async (cheque) => {
    const id = idInstrumentoFisico(cheque);
    if (!id) return;
    setDetalle(cheque);
    setHistorial([]);
    setErrorDetalle("");
    setLoadingDetalle(true);
    try {
      const data = await getChequeFisicoById(id, {
        origen_cheque: queryOrigenCheque(cheque),
      });
      const ch = data?.cheque || cheque;
      setDetalle({
        ...cheque,
        ...ch,
        acciones: ch.acciones || data?.acciones || cheque.acciones,
      });
      setHistorial(
        Array.isArray(data?.historial) && data.historial.length
          ? data.historial
          : extraerHistorialFisico(data, ch)
      );
    } catch (err) {
      setErrorDetalle(err.message || "No se pudo cargar el detalle");
    } finally {
      setLoadingDetalle(false);
    }
  }, []);

  const seleccionados = useMemo(
    () =>
      cheques.filter((c) =>
        rowSelection[idFilaCheque(c, idInstrumentoFisico(c))]
      ),
    [cheques, rowSelection]
  );

  const seleccionEnviables = useMemo(
    () => seleccionados.filter((c) => puedeEnviarCc(c)),
    [seleccionados]
  );

  const seleccionRecibibles = useMemo(
    () => seleccionados.filter((c) => puedeRecibirCc(c)),
    [seleccionados]
  );

  const refrescarTrasAccion = () => {
    setRowSelection({});
    setDetalle(null);
    setHistorial([]);
    cargarResumen();
    cargarSucursales();
    cargar();
  };

  const onAltaRechazoOk = (data) => {
    const listaNegra = data?.lista_negra;
    const extraListaNegra = listaNegra?.cuit
      ? ` — CUIT ${listaNegra.cuit} en lista negra`
      : "";
    setToast({
      open: true,
      message:
        (data?.msg || "Rechazo registrado — sigue el circuito de ND") +
        extraListaNegra,
      severity: "success",
    });
    refrescarTrasAccion();
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: "nro_cheque",
        header: "N° cheque",
        size: 70,
        maxSize: 70,
      },
      {
        accessorKey: "id_cheque",
        header: "ID cheque",
        size: 70,
        maxSize: 70,
        Cell: ({ cell, row }) => {
          const val =
            cell.getValue() ||
            (idInstrumentoFisico(row.original)
              ? `REC-${idInstrumentoFisico(row.original)}`
              : "");
          return (
            <Typography variant="body2" noWrap title={val} sx={{ fontSize: 13 }}>
              {val || "—"}
            </Typography>
          );
        },
      },
      {
        accessorKey: "banco_emisor",
        header: "Banco",
        size: 100,
        maxSize: 110,
        Cell: ({ cell }) => (
          <Typography
            variant="body2"
            noWrap
            title={cell.getValue()}
            sx={{ maxWidth: 100, fontSize: 13 }}
          >
            {cell.getValue() || "—"}
          </Typography>
        ),
      },
      {
        accessorKey: "importe",
        header: "Importe",
        size: 92,
        maxSize: 102,
        Cell: ({ cell }) => (
          <Typography variant="body2" noWrap sx={{ fontSize: 13 }}>
            {formatMoneda(cell.getValue())}
          </Typography>
        ),
      },
      {
        accessorKey: "fecha_pago",
        header: "Fecha pago",
        size: 80,
        maxSize: 88,
        Cell: ({ cell }) => formatearFecha(cell.getValue()),
      },
      {
        accessorKey: "estado",
        header: "Estado",
        size: 82,
        maxSize: 92,
        Cell: ({ cell }) => {
          const label = labelEstadoFisico(cell.getValue());
          return (
            <Chip
              label={label}
              title={label}
              size="small"
              color={colorEstadoFisico(cell.getValue())}
              variant="outlined"
              sx={{ maxWidth: "100%", "& .MuiChip-label": { px: 0.75 } }}
            />
          );
        },
      },
      {
        accessorKey: "origen_cheque",
        header: "Origen",
        size: 72,
        maxSize: 80,
        Cell: ({ row }) => (
          <Chip
            label={abrevOrigenCheque(row.original)}
            title={labelOrigenCheque(row.original)}
            size="small"
            color={colorOrigenCheque(row.original)}
            variant="outlined"
            sx={{ maxWidth: "100%", "& .MuiChip-label": { px: 0.75 } }}
          />
        ),
      },
      {
        accessorKey: "estado_custodia",
        header: "Custodia",
        size: 104,
        maxSize: 115,
        Cell: ({ row }) => {
          const c = custodiaDe(row.original);
          return (
            <Chip
              label={labelCustodia(c)}
              size="small"
              color={colorCustodia(c)}
              variant="outlined"
              sx={{ maxWidth: "100%", "& .MuiChip-label": { px: 0.75 } }}
            />
          );
        },
      },
      ...(esVistaTransito
        ? [
            {
              id: "sucursal_origen",
              header: "Suc. origen",
              size: 100,
              maxSize: 115,
              Cell: ({ row }) => (
                <Typography variant="body2" noWrap sx={{ fontSize: 13 }}>
                  {sucursalLabelCheque(row.original)}
                </Typography>
              ),
            },
          ]
        : []),
      {
        accessorKey: "recibido_de",
        header: "Recibido de",
        size: 118,
        maxSize: 128,
        Cell: ({ row }) => {
          const nombre =
            row.original.recibido_de ||
            row.original.cliente_razon_social ||
            row.original.proveedor_razon_social ||
            "—";
          return (
            <Box sx={{ maxWidth: 118 }}>
              <Typography
                variant="body2"
                noWrap
                title={nombre}
                sx={{ fontSize: 13 }}
              >
                {nombre}
              </Typography>
              <Typography variant="caption" color="text.secondary" noWrap>
                {formatCuit(row.original.cuit_recibido)}
              </Typography>
            </Box>
          );
        },
      },
      {
        accessorKey: "destino",
        header: "Destino",
        size: 62,
        maxSize: 72,
        Cell: ({ cell }) => cell.getValue() || "—",
      },
      {
        id: "dias",
        header: "Días",
        size: 52,
        maxSize: 60,
        Cell: ({ row }) => {
          const dias = diasDesdeEmision(row.original);
          if (dias == null) return "—";
          const alerta = row.original.alerta_vencimiento;
          return (
            <Chip
              label={dias}
              size="small"
              color={colorAlertaVencimiento(alerta)}
              variant={alerta ? "filled" : "outlined"}
              sx={{ minWidth: 36, "& .MuiChip-label": { px: 0.5 } }}
            />
          );
        },
      },
      {
        id: "acciones",
        header: "Acciones",
        size: 185,
        maxSize: 220,
        enableSorting: false,
        Cell: ({ row }) => {
          const cheque = row.original;
          return (
            <Stack
              direction="row"
              spacing={0.25}
              flexWrap="wrap"
              useFlexGap
              onClick={(e) => e.stopPropagation()}
              sx={{ maxWidth: 185 }}
            >
              {puedeDepositar(cheque) ? (
                <Button
                  size="small"
                  variant="outlined"
                  sx={{ minWidth: 0, px: 0.75, fontSize: 12 }}
                  onClick={() => setModalDepositar({ open: true, cheque })}
                >
                  Depositar
                </Button>
              ) : null}
              {puedeEnviarCc(cheque) ? (
                <Button
                  size="small"
                  variant="outlined"
                  color="info"
                  title="Enviar a Casa Central"
                  sx={{ minWidth: 0, px: 0.75, fontSize: 12 }}
                  onClick={() =>
                    setModalEnviar({ open: true, cheques: [cheque] })
                  }
                >
                  A C.Central
                </Button>
              ) : null}
              {puedeRecibirCc(cheque) ? (
                <Button
                  size="small"
                  variant="outlined"
                  color="success"
                  sx={{ minWidth: 0, px: 0.75, fontSize: 12 }}
                  onClick={() =>
                    setModalRecibir({ open: true, cheques: [cheque] })
                  }
                >
                  Recibir
                </Button>
              ) : null}
              {puedeFaltante(cheque) ? (
                <Button
                  size="small"
                  variant="outlined"
                  color="warning"
                  sx={{ minWidth: 0, px: 0.75, fontSize: 12 }}
                  onClick={() => setModalFaltante({ open: true, cheque })}
                >
                  Faltante
                </Button>
              ) : null}
              {puedeCorregir(cheque) ? (
                <Button
                  size="small"
                  variant="outlined"
                  sx={{ minWidth: 0, px: 0.75, fontSize: 12 }}
                  onClick={() => setModalCorregir({ open: true, cheque })}
                >
                  Corregir
                </Button>
              ) : null}
              {puedeAltaRechazo(cheque) ? (
                <Button
                  size="small"
                  variant="outlined"
                  color="error"
                  sx={{ minWidth: 0, px: 0.75, fontSize: 12 }}
                  onClick={() => setModalAltaRechazo({ open: true, cheque })}
                >
                  Rechazo
                </Button>
              ) : null}
            </Stack>
          );
        },
      },
    ],
    [esVistaTransito]
  );

  const faltaSucursal = !esVistaTransito && !idSucursal;

  return (
    <>
      <Navbar />
      <Container maxWidth="xl" sx={{ pt: "20px", pb: 4, px: 2 }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          alignItems={{ xs: "stretch", sm: "center" }}
          justifyContent="space-between"
          spacing={2}
          sx={{ mb: 2 }}
        >
          <Box>
            <Typography variant="h5" fontWeight={700}>
              Cheques físicos
              {esCasaCentralActiva ? " — Casa Central" : ""}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {esVistaTransito
                ? "Cheques en tránsito hacia Casa Central. Recibilos o marcá faltante; en tránsito no se depositan ni se usan en OP."
                : "El cheque físico se mueve a mano. Acá se registra dónde está y qué se hizo. El alta es el recibo de Cta Cte (instrumento tipo 3)."}
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {badges.alerta_vencimiento_proximo > 0 ||
            badges.alerta_vencimiento_vencido > 0 ? (
              <>
                {badges.alerta_vencimiento_proximo > 0 ? (
                  <Chip
                    label={`Próximo a vencer (${badges.alerta_vencimiento_proximo})`}
                    size="small"
                    color="warning"
                    clickable
                    variant={
                      filtrosAplicados.alerta_vencimiento ===
                      ALERTA_VENCIMIENTO_CHEQUE.PROXIMO
                        ? "filled"
                        : "outlined"
                    }
                    onClick={() =>
                      aplicarFiltroAlerta(ALERTA_VENCIMIENTO_CHEQUE.PROXIMO)
                    }
                  />
                ) : null}
                {badges.alerta_vencimiento_vencido > 0 ? (
                  <Chip
                    label={`Vencidos (${badges.alerta_vencimiento_vencido})`}
                    size="small"
                    color="error"
                    clickable
                    variant={
                      filtrosAplicados.alerta_vencimiento ===
                      ALERTA_VENCIMIENTO_CHEQUE.VENCIDO
                        ? "filled"
                        : "outlined"
                    }
                    onClick={() =>
                      aplicarFiltroAlerta(ALERTA_VENCIMIENTO_CHEQUE.VENCIDO)
                    }
                  />
                ) : null}
              </>
            ) : null}
            {menus.listaNegra ? (
              <Button
                component={RouterLink}
                to={rutaListaNegra()}
                variant="outlined"
                size="small"
                color="warning"
              >
                Lista negra
              </Button>
            ) : null}
          </Stack>
        </Stack>

        {esCasaCentralActiva ? (
          <Tabs
            value={vistaCc}
            onChange={(_e, value) => {
              setVistaCc(value);
              setPage(0);
              setRowSelection({});
              setCheques([]);
            }}
            sx={{ mb: 2, borderBottom: 1, borderColor: "divider" }}
          >
            <Tab label="Cartera Casa Central" value={VISTA_CC.CARTERA} />
            <Tab label="En tránsito" value={VISTA_CC.TRANSITO} />
          </Tabs>
        ) : null}

        <Paper
          component="form"
          onSubmit={aplicarFiltros}
          elevation={1}
          sx={{ p: 2, mb: 2, backgroundColor: "#fff" }}
        >
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{ mb: 1.5 }}
          >
            <FilterListIcon color="action" fontSize="small" />
            <Typography variant="subtitle2" fontWeight={600}>
              Filtros
            </Typography>
          </Stack>
          <Grid container spacing={2} alignItems="flex-end">
            {!esVistaTransito ? (
              <Grid item xs={12} sm={6} md={2.5}>
                <Autocomplete
                  fullWidth
                  size="small"
                  options={sucursales}
                  loading={cargandoSucursales}
                  disabled={cargandoSucursales}
                  disableClearable={Boolean(sucursalActiva)}
                  value={sucursalActiva}
                  onChange={(_e, sucursal) => {
                    if (!sucursal) return;
                    setIdSucursal(String(idSucursalDe(sucursal)));
                    setPage(0);
                    setRowSelection({});
                  }}
                  getOptionLabel={textoSucursal}
                  isOptionEqualToValue={(a, b) =>
                    String(idSucursalDe(a)) === String(idSucursalDe(b))
                  }
                  noOptionsText="Sin sucursales"
                  renderOption={(props, option) => {
                    const { key, ...optionProps } = props;
                    return (
                      <Box
                        component="li"
                        key={key ?? idSucursalDe(option)}
                        {...optionProps}
                      >
                        {labelSucursalConAlertas(option)}
                      </Box>
                    );
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      required
                      label="Sucursal"
                      placeholder="Buscar sucursal..."
                      size="small"
                    />
                  )}
                />
              </Grid>
            ) : (
              <Grid item xs={12} sm={6} md={2.5}>
                <Autocomplete
                  fullWidth
                  size="small"
                  options={sucursales}
                  loading={cargandoSucursales}
                  disabled={cargandoSucursales}
                  value={
                    sucursales.find(
                      (s) =>
                        String(idSucursalDe(s)) ===
                        String(filtros.id_sucursal_origen)
                    ) || null
                  }
                  onChange={(_e, sucursal) =>
                    setFiltros((prev) => ({
                      ...prev,
                      id_sucursal_origen: sucursal
                        ? String(idSucursalDe(sucursal))
                        : "",
                    }))
                  }
                  getOptionLabel={(s) => (s ? nombreSucursalDe(s) : "")}
                  isOptionEqualToValue={(a, b) =>
                    String(idSucursalDe(a)) === String(idSucursalDe(b))
                  }
                  noOptionsText="Sin sucursales"
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Sucursal origen"
                      placeholder="Buscar sucursal..."
                      size="small"
                    />
                  )}
                />
              </Grid>
            )}
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                size="small"
                label="Buscar"
                placeholder="Nro, banco..."
                value={filtros.q}
                onChange={(e) =>
                  setFiltros((prev) => ({ ...prev, q: e.target.value }))
                }
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                select
                fullWidth
                size="small"
                label="Estado"
                value={filtros.estado}
                onChange={(e) =>
                  setFiltros((prev) => ({ ...prev, estado: e.target.value }))
                }
              >
                {ESTADOS_FILTRO_FISICOS.map((opt) => (
                  <MenuItem key={opt.value || "todos"} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                select
                fullWidth
                size="small"
                label="Origen"
                value={filtros.origen_cheque}
                onChange={(e) =>
                  setFiltros((prev) => ({
                    ...prev,
                    origen_cheque: e.target.value,
                  }))
                }
              >
                {ORIGEN_CHEQUE_FILTRO.map((opt) => (
                  <MenuItem key={opt.value || "todos"} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            {!esVistaTransito ? (
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  select
                  fullWidth
                  size="small"
                  label="Custodia"
                  value={filtros.estado_custodia}
                  onChange={(e) =>
                    setFiltros((prev) => ({
                      ...prev,
                      estado_custodia: e.target.value,
                    }))
                  }
                >
                  {CUSTODIA_FILTRO.map((opt) => (
                    <MenuItem key={opt.value || "todas"} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            ) : null}
            {!esVistaTransito ? (
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  select
                  fullWidth
                  size="small"
                  label="Alerta vencimiento"
                  value={filtros.alerta_vencimiento}
                  onChange={(e) =>
                    setFiltros((prev) => ({
                      ...prev,
                      alerta_vencimiento: e.target.value,
                    }))
                  }
                >
                  {ALERTA_VENCIMIENTO_FILTRO.map((opt) => (
                    <MenuItem key={opt.value || "todas"} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            ) : null}
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                size="small"
                label="Pago desde"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={filtros.fecha_pago_desde}
                onChange={(e) =>
                  setFiltros((prev) => ({
                    ...prev,
                    fecha_pago_desde: e.target.value,
                  }))
                }
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                size="small"
                label="Pago hasta"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={filtros.fecha_pago_hasta}
                onChange={(e) =>
                  setFiltros((prev) => ({
                    ...prev,
                    fecha_pago_hasta: e.target.value,
                  }))
                }
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <Stack direction="row" spacing={1}>
                <Button type="submit" variant="contained" size="small">
                  Buscar
                </Button>
                <Button variant="outlined" size="small" onClick={limpiarFiltros}>
                  Limpiar
                </Button>
              </Stack>
            </Grid>
          </Grid>
        </Paper>

        {error ? (
          <Alert
            severity="error"
            sx={{ mb: 2 }}
            action={
              <Button color="inherit" size="small" onClick={cargar}>
                Reintentar
              </Button>
            }
          >
            {error}
          </Alert>
        ) : null}

        {seleccionEnviables.length > 0 || seleccionRecibibles.length > 0 ? (
          <Stack
            direction="row"
            spacing={1}
            sx={{ mb: 2 }}
            flexWrap="wrap"
            useFlexGap
          >
            {seleccionEnviables.length > 0 ? (
              <Button
                size="small"
                variant="contained"
                onClick={() =>
                  setModalEnviar({ open: true, cheques: seleccionEnviables })
                }
              >
                Enviar C.Central ({seleccionEnviables.length})
              </Button>
            ) : null}
            {seleccionRecibibles.length > 0 ? (
              <Button
                size="small"
                variant="contained"
                color="success"
                onClick={() =>
                  setModalRecibir({ open: true, cheques: seleccionRecibibles })
                }
              >
                Recibir ({seleccionRecibibles.length})
              </Button>
            ) : null}
          </Stack>
        ) : null}

        {faltaSucursal && !cargandoSucursales ? (
          <Alert severity="info">Elegí una sucursal para ver la cartera.</Alert>
        ) : loading || cargandoSucursales ? (
          <Box display="flex" justifyContent="center" py={6}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <MaterialReactTable
              columns={columns}
              data={cheques}
              layoutMode="grid"
              getRowId={(row) =>
                idFilaCheque(row, idInstrumentoFisico(row) || row.id)
              }
              localization={MRT_Localization_ES}
              enableColumnActions={false}
              enableColumnFilters={false}
              enableDensityToggle={false}
              enableFullScreenToggle={false}
              enableHiding={false}
              enablePagination={false}
              enableRowSelection
              onRowSelectionChange={setRowSelection}
              state={{ rowSelection }}
              initialState={{ density: "compact" }}
              defaultColumn={{ size: 72, minSize: 36 }}
              displayColumnDefOptions={{
                "mrt-row-select": { size: 34, maxSize: 38, minSize: 30 },
              }}
              muiSelectCheckboxProps={{
                onClick: (e) => e.stopPropagation(),
              }}
              muiTableBodyRowProps={({ row }) => ({
                onClick: (e) => {
                  if (e.target?.closest?.("button, input, .MuiCheckbox-root")) {
                    return;
                  }
                  abrirDetalle(row.original);
                },
                sx: {
                  cursor: idInstrumentoFisico(row.original)
                    ? "pointer"
                    : "default",
                  ...filaSxAlertaVencimiento(
                    row.original.alerta_vencimiento,
                    row.index % 2 === 0
                  ),
                },
              })}
              muiTableHeadCellProps={{
                sx: {
                  fontSize: 13,
                  color: "white",
                  fontWeight: "normal",
                  backgroundColor: "#2471A3",
                  borderColor: "black",
                  py: 0.5,
                  px: 0.4,
                  whiteSpace: "nowrap",
                },
              }}
              muiTableBodyCellProps={{
                sx: {
                  borderColor: "black",
                  py: 0.35,
                  px: 0.4,
                  fontSize: 13,
                },
              }}
              muiTableProps={{
                sx: {
                  tableLayout: "fixed",
                  width: "100%",
                  borderCollapse: "collapse",
                },
              }}
              muiTablePaperProps={{
                elevation: 0,
                sx: {
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 2,
                },
              }}
              renderTopToolbarCustomActions={() => (
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Typography variant="subtitle1" fontWeight={600}>
                    {paginacion.total > 0
                      ? `${paginacion.total} cheque${
                          paginacion.total === 1 ? "" : "s"
                        }`
                      : "Sin resultados"}
                  </Typography>
                  <Button
                    size="small"
                    startIcon={<RefreshIcon />}
                    onClick={() => {
                      cargarResumen();
                      cargarSucursales();
                      cargar();
                    }}
                  >
                    Actualizar
                  </Button>
                </Stack>
              )}
              renderEmptyRowsFallback={() => (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ p: 3, textAlign: "center" }}
                >
                  {esVistaTransito
                    ? "No hay cheques en tránsito hacia Casa Central."
                    : "No hay cheques físicos con los filtros seleccionados."}
                </Typography>
              )}
            />

            <TablePagination
              component="div"
              count={paginacion.total}
              page={page}
              onPageChange={(_e, newPage) => {
                setPage(newPage);
                setRowSelection({});
              }}
              rowsPerPage={PAGE_SIZE}
              rowsPerPageOptions={[PAGE_SIZE]}
              labelDisplayedRows={({ from, to, count }) =>
                `${from}-${to} de ${count !== -1 ? count : `más de ${to}`}`
              }
            />
          </>
        )}
      </Container>

      <ModalDepositar
        open={modalDepositar.open}
        cheque={modalDepositar.cheque}
        sucursal={sucursalActiva}
        onClose={() => setModalDepositar({ open: false, cheque: null })}
        onOk={refrescarTrasAccion}
      />
      <ModalEnviarCC
        open={modalEnviar.open}
        cheques={modalEnviar.cheques}
        onClose={() => setModalEnviar({ open: false, cheques: [] })}
        onOk={refrescarTrasAccion}
      />
      <ModalRecibirCC
        open={modalRecibir.open}
        cheques={modalRecibir.cheques}
        onClose={() => setModalRecibir({ open: false, cheques: [] })}
        onOk={refrescarTrasAccion}
      />
      <ModalFaltante
        open={modalFaltante.open}
        cheque={modalFaltante.cheque}
        onClose={() => setModalFaltante({ open: false, cheque: null })}
        onOk={refrescarTrasAccion}
      />
      <ModalCorregir
        open={modalCorregir.open}
        cheque={modalCorregir.cheque}
        onClose={() => setModalCorregir({ open: false, cheque: null })}
        onOk={refrescarTrasAccion}
      />
      <ModalAltaRechazo
        open={modalAltaRechazo.open}
        cheque={modalAltaRechazo.cheque}
        postRechazo={postRechazoChequeFisico}
        onClose={() => setModalAltaRechazo({ open: false, cheque: null })}
        onOk={onAltaRechazoOk}
      />

      <Dialog
        open={Boolean(detalle)}
        onClose={() => setDetalle(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Detalle del cheque físico</DialogTitle>
        <DialogContent dividers>
          {loadingDetalle ? (
            <Box display="flex" justifyContent="center" py={3}>
              <CircularProgress size={28} />
            </Box>
          ) : (
            <>
              {errorDetalle ? (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  {errorDetalle}
                </Alert>
              ) : null}
              <Grid container spacing={1.5}>
                <CampoDetalle label="N° cheque" value={detalle?.nro_cheque} />
                <CampoDetalle
                  label="ID instrumento"
                  value={idInstrumentoFisico(detalle)}
                />
                <CampoDetalle label="Banco" value={detalle?.banco_emisor} />
                <CampoDetalle
                  label="Importe"
                  value={
                    detalle?.importe != null
                      ? formatMoneda(detalle.importe)
                      : "—"
                  }
                />
                <CampoDetalle
                  label="Fecha emisión"
                  value={formatearFecha(detalle?.fecha_emision)}
                />
                <CampoDetalle
                  label="Fecha pago"
                  value={formatearFecha(detalle?.fecha_pago)}
                />
                <CampoDetalle
                  label="Días desde emisión"
                  value={
                    detalle?.dias_desde_emision != null
                      ? detalle.dias_desde_emision
                      : diasDesdeEmision(detalle)
                  }
                />
                <CampoDetalle
                  label="Vencimiento vigencia"
                  value={formatearFecha(detalle?.fecha_vencimiento_vigencia)}
                />
                <CampoDetalle
                  label="Días para vencimiento"
                  value={
                    detalle?.dias_para_vencimiento != null
                      ? detalle.dias_para_vencimiento
                      : "—"
                  }
                />
                <CampoDetalle
                  label="Alerta vencimiento"
                  value={
                    labelAlertaVencimiento(detalle?.alerta_vencimiento) || "—"
                  }
                />
                <CampoDetalle
                  label="Estado"
                  value={labelEstadoFisico(detalle?.estado)}
                />
                <CampoDetalle
                  label="Origen"
                  value={labelOrigenCheque(detalle)}
                />
                {detalle?.id_orden_pago ? (
                  <CampoDetalle
                    label="Orden de pago"
                    value={detalle.id_orden_pago}
                  />
                ) : null}
                <CampoDetalle
                  label="Custodia"
                  value={labelCustodia(custodiaDe(detalle))}
                />
                <CampoDetalle
                  label="Sucursal"
                  value={sucursalLabelCheque(detalle)}
                />
                <CampoDetalle
                  label="Recibido de"
                  value={
                    detalle?.recibido_de || detalle?.cliente_razon_social || "—"
                  }
                />
                <CampoDetalle
                  label="CUIT recibido"
                  value={formatCuit(detalle?.cuit_recibido)}
                />
                <CampoDetalle
                  label="Librador"
                  value={detalle?.razon_social_librador}
                />
                <CampoDetalle
                  label="CUIT librador"
                  value={formatCuit(detalle?.cuit_librador)}
                />
                <CampoDetalle label="ID recibo" value={detalle?.id_recibo} />
                <CampoDetalle label="N° recibo" value={detalle?.nro_recibo} />
                <CampoDetalle label="Destino" value={detalle?.destino} />
                <CampoDetalle
                  label="Tipo depósito"
                  value={detalle?.tipo_deposito}
                />
                <CampoDetalle
                  label="Depósito destino"
                  value={detalle?.deposito_destino}
                />
                <CampoDetalle
                  label="Recaudadora"
                  value={detalle?.recaudadora}
                />
                <CampoDetalle label="CMC7" value={detalle?.cmc7} />
              </Grid>

              <Typography
                variant="subtitle2"
                fontWeight={600}
                sx={{ mt: 3, mb: 1 }}
              >
                Historial
              </Typography>
              {historial.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  Sin movimientos registrados.
                </Typography>
              ) : (
                <Stack spacing={1}>
                  {historial.map((ev, idx) => (
                    <Paper
                      key={ev.id || `${ev.fecha}-${idx}`}
                      variant="outlined"
                      sx={{ p: 1.25 }}
                    >
                      <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        flexWrap="wrap"
                        useFlexGap
                      >
                        <Typography variant="caption" color="text.secondary">
                          {formatearFechaHora(ev.fecha || ev.created_at)}
                        </Typography>
                        {ev.estado ? (
                          <Chip
                            size="small"
                            label={labelEstadoFisico(ev.estado)}
                            color={colorEstadoFisico(ev.estado)}
                            variant="outlined"
                          />
                        ) : null}
                        {ev.estado_custodia || ev.custodia ? (
                          <Chip
                            size="small"
                            label={labelCustodia(
                              ev.estado_custodia || ev.custodia
                            )}
                            color={colorCustodia(
                              ev.estado_custodia || ev.custodia
                            )}
                            variant="outlined"
                          />
                        ) : null}
                        {ev.accion || ev.evento ? (
                          <Typography variant="body2" fontWeight={600}>
                            {labelAccionFisico(ev.accion || ev.evento)}
                          </Typography>
                        ) : null}
                      </Stack>
                      <Typography variant="body2" sx={{ mt: 0.5 }}>
                        {ev.observacion || ev.obs || ev.observaciones || "—"}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {ev.user || ev.usuario || ev.username || ""}
                      </Typography>
                    </Paper>
                  ))}
                </Stack>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetalle(null)}>Cerrar</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={toast.open}
        autoHideDuration={8000}
        onClose={() => setToast((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={toast.severity}
          onClose={() => setToast((prev) => ({ ...prev, open: false }))}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </>
  );
}
